// src/components/Onboarding/canvas/canvasUtils.js
// Utility functions for canvas calculations and coordinate mapping

import { MAX_DPR, MAX_BACKING_PIXELS } from './canvasConstants';

/**
 * Easing function: ease-in-out cubic
 */
export function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Calculate effective DPR capped to MAX_DPR and 2MP limit
 */
export function getEffectiveDPR(cssWidth, cssHeight) {
  const rawDPR = window.devicePixelRatio || 1;
  let dpr = Math.min(rawDPR, MAX_DPR);
  
  // Check if backing store would exceed 2MP
  const backingPixels = Math.round(cssWidth * dpr) * Math.round(cssHeight * dpr);
  if (backingPixels > MAX_BACKING_PIXELS) {
    // Scale down DPR to fit within limit
    dpr = Math.sqrt(MAX_BACKING_PIXELS / (cssWidth * cssHeight));
    dpr = Math.max(1, Math.min(dpr, MAX_DPR)); // Clamp between 1 and MAX_DPR
  }
  
  return dpr;
}

/**
 * Calculate object-fit bounds (like CSS object-fit: contain/cover)
 * objectPosition format: { x: 0-1, y: 0-1 } where 0.5 is center (like CSS object-position: 50% 50%)
 */
export function calculateObjectFit(imageWidth, imageHeight, canvasWidth, canvasHeight, mode = 'contain', objectPosition = { x: 0.5, y: 0.5 }) {
  if (!imageWidth || !imageHeight || !canvasWidth || !canvasHeight) {
    return { x: 0, y: 0, width: canvasWidth, height: canvasHeight, scale: 1 };
  }

  const imageAspect = imageWidth / imageHeight;
  const canvasAspect = canvasWidth / canvasHeight;

  let drawWidth, drawHeight, drawX, drawY, scale;

  if (mode === 'fill') {
    // Stretch to fill
    drawWidth = canvasWidth;
    drawHeight = canvasHeight;
    drawX = 0;
    drawY = 0;
    scale = Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight);
  } else if (mode === 'cover') {
    // Fill canvas, crop if needed
    if (imageAspect > canvasAspect) {
      drawHeight = canvasHeight;
      drawWidth = imageAspect * drawHeight;
      scale = canvasHeight / imageHeight;
    } else {
      drawWidth = canvasWidth;
      drawHeight = drawWidth / imageAspect;
      scale = canvasWidth / imageWidth;
    }
    // Use objectPosition to determine alignment (0 = left/top, 0.5 = center, 1 = right/bottom)
    const overflowX = drawWidth - canvasWidth;
    const overflowY = drawHeight - canvasHeight;
    drawX = -overflowX * objectPosition.x;
    drawY = -overflowY * objectPosition.y;
  } else {
    // contain (default) - fit entire image, letterbox if needed
    if (imageAspect > canvasAspect) {
      drawWidth = canvasWidth;
      drawHeight = drawWidth / imageAspect;
      scale = canvasWidth / imageWidth;
    } else {
      drawHeight = canvasHeight;
      drawWidth = imageAspect * drawHeight;
      scale = canvasHeight / imageHeight;
    }
    // Center by default (objectPosition not applicable in contain mode as no cropping occurs)
    drawX = (canvasWidth - drawWidth) / 2;
    drawY = (canvasHeight - drawHeight) / 2;
  }

  return { x: drawX, y: drawY, width: drawWidth, height: drawHeight, scale };
}

/**
 * Convert crop coordinates from image space to mask space
 * Since masks are at MASK_SCALE (50%) of image resolution, we need this mapping
 * @param {Object} cropPx - Crop rect in image pixel space {x, y, width, height}
 * @param {number} imageW - Full image width in pixels
 * @param {number} imageH - Full image height in pixels
 * @param {number} maskW - Mask canvas width
 * @param {number} maskH - Mask canvas height
 * @returns {Object} Crop rect in mask pixel space
 */
export function cropImageToMask(cropPx, imageW, imageH, maskW, maskH) {
  const scaleX = maskW / imageW;
  const scaleY = maskH / imageH;
  return {
    x: cropPx.x * scaleX,
    y: cropPx.y * scaleY,
    width: cropPx.width * scaleX,
    height: cropPx.height * scaleY,
  };
}

/**
 * Create coordinate mapping functions for overlay drawing
 */
export function createCoordinateMappers(srcRect, fit) {
  const mapX = (x) => {
    const relX = (x - srcRect.x) / srcRect.width;
    return fit.x + relX * fit.width;
  };

  const mapY = (y) => {
    const relY = (y - srcRect.y) / srcRect.height;
    return fit.y + relY * fit.height;
  };

  const mapSize = (size, dimension) => {
    return (size / (dimension === 'width' ? srcRect.width : srcRect.height)) * 
           (dimension === 'width' ? fit.width : fit.height);
  };

  // Check if overlay is visible in current crop region
  const isVisible = (x, y, width = 0, height = 0) => {
    // For points/circles, just check if center is in bounds
    if (width === 0 && height === 0) {
      return x >= srcRect.x && x <= srcRect.x + srcRect.width &&
             y >= srcRect.y && y <= srcRect.y + srcRect.height;
    }
    // For rects, check if any part overlaps with srcRect
    return !(x + width < srcRect.x || x > srcRect.x + srcRect.width ||
             y + height < srcRect.y || y > srcRect.y + srcRect.height);
  };

  return { mapX, mapY, mapSize, isVisible };
}
